import * as React from "react";

import { DynamicErrorHandler } from "@montana/shared/dynamic-error-handler";
import { IBlock } from "@montana/models/generated/cms.interface";

import { M00HeadlineAndTextBlock } from "./m00-headline-and-text-block/m-00headline-and-text-block.component";
import { M00MissingBlock } from "./m00-missing-block/m00-missing-block.component";
import { M00ProductEntrancesBlock } from "./m00-product-entrances-block/m00-product-entrances-block.component";
import { M00TextBlock } from "./m00-text-block/m00-text-block.component";
import { M01HeroBlock } from "./m01-hero-block/m01-hero-block.component";
import { M02CategoryCarouselBlock } from "./m02-category-carousel-block/m02-category-carousel-block.component";
import { M03VariantCarouselBlock } from "./m03-variant-carousel-block/m03-variant-carousel-block.component";
import { M031BaseModelCarouselBlock } from "./m031-base-model-carousel-block/m031-base-model-carousel-block.component";
import { M06StoryBlock } from "./m06-story-block/m06-story-block.component";
import { M07VideoBlock, M07YoutubeVideoBlock } from "./m07-video-block/m07-video-block.component";
import { M08ShopTheLookHeroBlock } from "./m08-shop-the-look-hero-block/m08-shop-the-look-hero-block.component";
import { M09ShopTheLookGalleryBlock } from "./m09-shop-the-look-gallery-block/m09-shop-the-look-gallery-block.component";
import { M10BannerBundleBlock } from "./m10-banner-bundle-block/m10-banner-bundle-block.component";
import { M12TextModuleBlock } from "./m12-text-module-block/m12-text-module-block.component";
import { M17NewsletterSignUpBlock } from "./m17-newsletter-sign-up/m17-newsletter-sign-up.component";
import { M171ContactMontanaBlock } from "./m17.1-contact-montana-block/m17.1-contact-montana-block.component";
import { M18UspBlock } from "./m18-usp-block/m18-usp-block.component";
import { M20ImagePatternBlock } from "./m20-image-pattern-block/m20-image-pattern-block.component";
import { M21QuoteBlock } from "./m21-quote-block/m21-quote-block.component";
import { M22ColoursAndSurfacesBlock } from "./m22-colours-and-surfaces-block/m22-colours-and-surfaces-block.component";
import { ColorMaterialDescriptionBlock } from "./m00-color-material-description-block/m00-color-material-description-block.component";
import {M25ColorCardCollection} from "@montana/blocks/m25-color-card-collection/m25-color-card-collection.component";

import cx from 'clsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BLOCKS: { [key: string]: React.ComponentType<any> } = {
    ColorMaterialDescriptionBlock: ColorMaterialDescriptionBlock,
    ProductEntranceContainerBlock: M00ProductEntrancesBlock,
    HeadlineBlock: M00HeadlineAndTextBlock,
    TextBlock: M00TextBlock,
    M01HeroBlock: M01HeroBlock,
    M02CategoryCarousel: M02CategoryCarouselBlock,
    M03VariantCarousel: M03VariantCarouselBlock,
    M031BaseModelCarousel: M031BaseModelCarouselBlock,
    M06StoryBlock: M06StoryBlock,
    M07VideoBlock: M07VideoBlock,
    M07YoutubeVideoBlock: M07YoutubeVideoBlock,
    M08BundleShopTheLookBlock: M08ShopTheLookHeroBlock,
    M09ShopTheLookGallery: M09ShopTheLookGalleryBlock,
    M10BannerBundleBlock: M10BannerBundleBlock,
    M12TextBlock: M12TextModuleBlock,
    M17Newsletter: M17NewsletterSignUpBlock,
    M171ContactMontana: M171ContactMontanaBlock,
    M18USPPromoBar: M18UspBlock,
    M20ImageBlock: M20ImagePatternBlock,
    M21QuoteBlock: M21QuoteBlock,
    M22ColourAndSurfacesBlock: M22ColoursAndSurfacesBlock,
    M25ColorCardCollection: M25ColorCardCollection
};

export interface IRenderingBlock extends IBlock {
    className?: string;
    domId?: string;
}

interface IDynamicBlocks {
    blocks?: IRenderingBlock[] | JSX.Element;
    isFirstElementOnPage?: boolean;
}

const breakContainer = (blockType: string): boolean => {
    switch (blockType) {
        case 'M01HeroBlock':
        case 'M02CategoryCarousel':
        case 'M03VariantCarousel':
        case 'M031BaseModelCarousel':
        case 'M07YoutubeVideoBlock':
        case 'M08BundleShopTheLookBlock':
        case 'M10BannerBundleBlock':
        case 'M20ImageBlock':
        case 'M21QuoteBlock':
            return true;
        default:
            return false;
    }
}

function breakContainerNarrow(kind: string): boolean {
    return kind === 'M07VideoBlock';
}

function kindClass(kind: string) {
    return kind.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function noSpaceAfter(kind: string, next?: IBlock) {
    const colorMaterialKind = 'ColorMaterialDescriptionBlock';
    return next?.kind === colorMaterialKind && kind === colorMaterialKind;
}

export const DynamicBlocks: React.VFC<IDynamicBlocks> = ({ blocks, isFirstElementOnPage = false }) => {
    // If no blocks, no render.
    if (!blocks) return null;

    // If blocks is NOT an array, we'll assume it to be pre-rendered.
    if (!Array.isArray(blocks)) return blocks;

    return <>{
        blocks.map((block, index) => {
            const Block = BLOCKS[block.kind];

            if (!Block) {
                return <M00MissingBlock key={`block--${index}`} kind={block.kind} />;
            }

            return (
                <DynamicErrorHandler {...block} key={`block--${index}`}>
                    <div className={cx('app__block', `app__block--${kindClass(block.kind)}`, {
                        'app__block--first': index === 0,
                        'app__block--last': index === blocks.length - 1,
                        'app__block--break-container': breakContainer(block.kind),
                        'app__block--break-container-narrow': breakContainerNarrow(block.kind),
                        'app__block--no-space-after': noSpaceAfter(block.kind, blocks[index +1]),
                    })} id={block.domId}>
                        <Block {...block} isFirstOnPage={index === 0 && isFirstElementOnPage} />
                    </div>
                </DynamicErrorHandler>
            );
        })
    }</>;
};
